import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  onAnimationEnd,
  removeWillChangeOnAnimationEnd,
} from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the current submenu or the main menu drawer
   * when Escape is pressed.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean}
   * Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   *
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) {
      return this.refs.details;
    }

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or submenu.
   *
   * For a drill-in submenu, focus moves into the newly
   * opened panel after the transition.
   *
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);

    const summary =
      details.querySelector(':scope > summary') ??
      details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add(
          'menu-drawer--has-submenu-opened'
        );
      }

      const drawer =
        details.querySelector(
          ':scope > .menu-drawer, :scope > .menu-drawer__submenu'
        ) ??
        details.querySelector(
          '.menu-drawer, .menu-drawer__submenu'
        );

      onAnimationEnd(
        drawer || details,
        () => {
          trapFocus(details);

          if (target) {
            const submenu =
              details.querySelector(
                ':scope > .menu-drawer__submenu'
              ) ??
              details.querySelector(
                '.menu-drawer__submenu'
              );

            const firstControl = submenu?.querySelector(
              '.menu-drawer__back-button, a[href], button:not([disabled])'
            );

            if (firstControl instanceof HTMLElement) {
              firstControl.focus();
            }
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   *
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open.
   *
   * When a submenu closes, focus returns to the control
   * that opened it.
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary =
      details.querySelector(':scope > summary') ??
      details.querySelector('summary');

    if (!summary) return;

    const isMainDrawer = details === this.refs.details;

    summary.setAttribute('aria-expanded', 'false');

    details.classList.remove('menu-open');

    this.refs.menuDrawer.classList.remove(
      'menu-drawer--has-submenu-opened'
    );

    const drawer =
      details.querySelector(
        ':scope > .menu-drawer, :scope > .menu-drawer__submenu'
      ) ??
      details.querySelector(
        '.menu-drawer, .menu-drawer__submenu'
      );

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);

        if (isMainDrawer) {
          removeTrapFocus();

          const openDetails = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );

          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);

          if (summary instanceof HTMLElement) {
            summary.focus();
          }
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend listeners to animated elements
   * so will-change can be removed afterwards.
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(
      '.menu-drawer__animated-element'
    );

    allAnimated.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }

  /**
   * Temporarily disable accordion animations while
   * opening the drawer.
   *
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      'accordion-custom .details-content'
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add(
          'details-content--no-animation'
        );
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove(
            'details-content--no-animation'
          );
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define(
    'header-drawer',
    HeaderDrawer
  );
}

/**
 * Reset an open details element to its original state.
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');

  element
    .querySelector(':scope > summary')
    ?.setAttribute('aria-expanded', 'false');
}